import React, { useState } from 'react';
import Link from 'next/link';
import { firebase } from '../firebaseClient';
import { useSession } from '../lib/auth';
import Loading from '../components/Loading/Loading';
import { useForm } from 'react-hook-form';
import Button from '../components/Button';
import { useToasts } from 'react-toast-notifications';
import { buildClient } from '../lib/api';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import Head from 'next/head';

type EmailInput = {
  name: string;
  email: string;
  password: string;
};

const InvitedDetails: React.FC<any> = ({ inviteCode }) => {
  const { data: invite } = useSWR(`/invites/${inviteCode}`);

  return (
    <>
      {invite ? (
        <div className="mb-4">
          <span className="font-bold">
            {invite.invited_by.name || invite.invited_by.email}
          </span>{' '}
          invited you to join the{' '}
          <span className="font-bold">{invite.organization.name}</span>{' '}
          organization.
        </div>
      ) : null}
    </>
  );
};

const Register: React.FunctionComponent = () => {
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const router = useRouter();
  const { invite: inviteCode } = router.query;
  const [registering, setRegistering] = useState(false);

  const { user, loading: sessionLoading, authenticated } = useSession();
  const { addToast } = useToasts();
  const {
    register,
    handleSubmit,
    errors,
    formState: { isSubmitting },
  } = useForm<EmailInput>();

  const createUser = async (user: firebase.User, name: string = '') => {
    setRegistering(true);
    const token = await user.getIdToken();
    try {
      await buildClient(token, {
        withCredentials: true,
      }).post('/profile', { user: { invite_code: inviteCode, name: name } });
      await firebase
        .auth()
        .currentUser.sendEmailVerification()
        .then(() => {
          console.log('sent!');
        })
        .catch((e) => console.log(e));
      setRegistering(false);
    } catch (e) {
      console.log('here', e);
      addToast(`Something went wrong 🤔: ${e}`, { appearance: 'error' });
    }
  };

  const onSubmitEmailPassword = async ({ email, password, name }) => {
    firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(async (userCredential) => {
        createUser(userCredential.user, name);
      })
      .catch((error) => {
        console.log('or', error);
        addToast(`Something went wrong 🤔: ${error}`, { appearance: 'error' });
      });
  };

  const registerUserWithGoogle = () => {
    let provider = new firebase.auth.GoogleAuthProvider();
    firebase
      .auth()
      .signInWithPopup(provider)
      .then(async (userCredential) => {
        createUser(userCredential.user);
      })
      .catch(() => {
        setLoadingGoogle(false);
      });
  };

  if (authenticated && !registering) {
    const route = user.organization?.slug
      ? `/org/${user.organization.slug}`
      : '/onboarding';

    window.location.href = route;
  }

  return (
    <div>
      <Head>
        <title>Lampant | Register</title>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <div className="flex flex-col items-center justify-center mx-auto bg-indigo-400 text-white min-h-screen py-10">
        {sessionLoading || authenticated ? (
          <Loading />
        ) : (
          <div className="w-full px-4 md:w-1/4">
            <Link href="/">
              <a>
                <img
                  className="w-80 pb-10 block mx-auto"
                  src="/images/logo/lampant.png"
                  alt=""
                />
              </a>
            </Link>

            {inviteCode ? <InvitedDetails inviteCode={inviteCode} /> : null}

            <div>
              <form onSubmit={handleSubmit(onSubmitEmailPassword)}>
                <div className="text-xs my-2">
                  <p>Your name</p>
                  {errors.name && (
                    <p className="text-red-900 font-medium">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <input
                  type="text"
                  name="name"
                  ref={register({
                    required: true,
                  })}
                  className="rounded-md w-full h-12 text-2xl border-2 border-indigo-50 text-indigo-700 px-2"
                />

                <div className="text-xs my-2">
                  <p>Your email</p>
                  {errors.email && (
                    <p className="text-red-900 font-medium">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <input
                  type="text"
                  name="email"
                  ref={register({
                    required: true,
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: 'Invalid email address',
                    },
                  })}
                  className="rounded-md w-full h-12 border-2 border-indigo-50 text-indigo-700 px-2"
                />

                <div className="text-xs my-2">
                  <p>Choose a password</p>
                  {errors.password && (
                    <p className="text-red-900 font-medium">
                      {errors.password.message}
                    </p>
                  )}
                </div>
                <input
                  type="password"
                  name="password"
                  ref={register({
                    required: true,
                    minLength: { value: 8, message: 'Minimum 8 chars' },
                  })}
                  className="rounded-md w-full h-12 text-2xl border-2 border-indigo-50 text-indigo-700 px-2"
                />

                <div className="rounded-md shadow mt-6">
                  <Button
                    type="submit"
                    isLoading={isSubmitting}
                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-800 hover:bg-indigo-600 md:py-4 md:text-lg md:px-10"
                  >
                    Sign up
                  </Button>
                </div>
              </form>
            </div>

            <div className="flex flex-row items-center mt-4">
              <div className="border-indigo-100 border-b-2 flex-1" />
              <span className="px-4 block">or</span>
              <div className="border-indigo-100 border-b-2 flex-1" />
            </div>

            <div className="rounded-md shadow mt-6">
              <button
                type="button"
                className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 md:py-4 md:text-lg md:px-10"
                onClick={() => {
                  setLoadingGoogle(true);
                  registerUserWithGoogle();
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="26"
                  height="26"
                  viewBox="0 0 48 48"
                  className="abcRioButtonSvg"
                >
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                  <path fill="none" d="M0 0h48v48H0z" />
                </svg>
                <span className="px-2">
                  {!loadingGoogle ? 'Sign up with Google' : 'Loading...'}
                </span>
              </button>
            </div>

            <div className="text-xs text-center mt-10">
              <p className="p-2">
                By clicking the button, you agree to our{' '}
                <Link href="/terms">
                  <a className="text-yellow-400">Terms of Service</a>
                </Link>{' '}
                and have read and acknowledge our{' '}
                <Link href="/privacy">
                  <a className="text-yellow-400">Privacy Policy</a>
                </Link>
                .
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
